import React, { useState, useEffect, useRef, useCallback } from "react";
import { Modal } from 'antd';
import { db } from "./firebase";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { collection, serverTimestamp, doc, setDoc, getDocs, deleteDoc, addDoc, writeBatch } from "firebase/firestore";
import Papa from "papaparse";

export default function AdminDailyQuotes() {
    const [formData, setFormData] = useState({
        text: "",
        author: "",
        category: "General",
        textColor: "#ffffff",
        backgroundType: "image", // "image" or "color"
        backgroundColor: "#FFF7F3" // default Off-White
    });
    const [imageFile, setImageFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [quotes, setQuotes] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [previewOpen, setPreviewOpen] = useState(false);

    // Bulk Upload State
    const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
    const [csvFile, setCsvFile] = useState(null);
    const [bulkImages, setBulkImages] = useState([]); // Kept for legacy support, not strictly needed
    const [bulkLoading, setBulkLoading] = useState(false);
    const [bulkProgress, setBulkProgress] = useState(0);

    // Infinite scroll & search state
    const [searchQuery, setSearchQuery] = useState("");
    const [visibleCount, setVisibleCount] = useState(10);
    const observer = useRef();

    const storage = getStorage();

    const fetchQuotes = async () => {
        try {
            const querySnapshot = await getDocs(collection(db, "dailyQuotes"));
            const fetched = querySnapshot.docs.map(doc => ({ ...doc.data(), dbId: doc.id }));
            // sort descending by time if available, otherwise just leave as is
            fetched.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
            setQuotes(fetched);
        } catch (error) {
            console.error("Failed to fetch quotes:", error);
        }
    };

    useEffect(() => {
        fetchQuotes();
    }, []);

    const handleInput = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleFileChange = (e) => {
        if (e.target.files[0]) {
            setImageFile(e.target.files[0]);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage("");

        try {
            let imageUrl = formData.image || "";

            if (formData.backgroundType === "image") {
                if (imageFile) {
                    const imageRef = ref(storage, `dailyQuotes/${Date.now()}_${imageFile.name}`);
                    await uploadBytes(imageRef, imageFile);
                    imageUrl = await getDownloadURL(imageRef);
                }
                // We no longer strictly require an image since the frontend will auto-assign
                // But if they provided one, we save it.
            } else {
                imageUrl = ""; // reset image if background is color
            }

            const quoteData = {
                text: formData.text,
                author: formData.author || "",
                category: formData.category || "General",
                textColor: formData.textColor || "#ffffff",
                updatedAt: serverTimestamp()
            };

            if (formData.backgroundType === "image") {
                quoteData.image = imageUrl; // Can be empty, frontend will handle fallback
            } else {
                quoteData.backgroundColor = formData.backgroundColor;
            }

            if (!editingId) {
                quoteData.createdAt = serverTimestamp();
                await addDoc(collection(db, "dailyQuotes"), quoteData);
                setMessage("Quote successfully added!");
            } else {
                await setDoc(doc(db, "dailyQuotes", editingId), quoteData, { merge: true });
                setMessage("Quote successfully updated!");
            }

            cancelEdit();
            fetchQuotes();
        } catch (error) {
            console.error("Error saving quote:", error);
            setMessage("Failed to save quote: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (q) => {
        setEditingId(q.dbId);
        setFormData({
            text: q.text || "",
            author: q.author || "",
            category: q.category || "General",
            image: q.image || "",
            textColor: q.textColor || "#ffffff",
            backgroundType: q.backgroundColor ? "color" : "image",
            backgroundColor: q.backgroundColor || "#FFF7F3"
        });
        setImageFile(null);
        setMessage("Editing quote.");
    };

    const handleDelete = async (q) => {
        if (!window.confirm("Are you sure you want to delete this quote?")) return;
        try {
            await deleteDoc(doc(db, "dailyQuotes", q.dbId));
            setMessage("Quote deleted.");
            fetchQuotes();
        } catch (error) {
            console.error("Error deleting:", error);
            setMessage("Failed to delete.");
        }
    };

    const handleDeleteAll = async () => {
        if (!window.confirm("WARNING: Are you sure you want to delete ALL daily quotes? This action cannot be undone!")) return;

        setLoading(true);
        setMessage("Deleting all quotes. This may take a minute...");
        try {
            const querySnapshot = await getDocs(collection(db, "dailyQuotes"));
            let totalDeleted = 0;
            const deletePromises = [];

            for (const docSnapshot of querySnapshot.docs) {
                deletePromises.push(deleteDoc(docSnapshot.ref));
                totalDeleted++;
                // Process in chunks of 50 to avoid rate limits
                if (deletePromises.length >= 50) {
                    await Promise.all(deletePromises);
                    deletePromises.length = 0; // Clear the array
                }
            }

            // Finish remaining
            if (deletePromises.length > 0) {
                await Promise.all(deletePromises);
            }

            setMessage(`Successfully deleted ${totalDeleted} quotes.`);
            fetchQuotes();
        } catch (error) {
            console.error("Error deleting all quotes:", error);
            setMessage("Failed to delete all quotes: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteByBatch = async () => {
        const uniqueBatches = [...new Set(quotes.filter(q => q.uploadFileName).map(q => q.uploadFileName))];
        if (uniqueBatches.length === 0) {
            alert("No CSV uploaded quotes found in the database yet. Try uploading a new CSV first.");
            return;
        }
        
        const batchName = window.prompt(`Enter the EXACT CSV filename to delete all quotes from it.\n\nAvailable uploaded files:\n${uniqueBatches.join('\n')}`);
        if (!batchName) return;

        const quotesToDelete = quotes.filter(q => q.uploadFileName === batchName);
        if (quotesToDelete.length === 0) {
            alert("No quotes found matching that exact filename.");
            return;
        }

        if (!window.confirm(`Are you sure you want to delete all ${quotesToDelete.length} quotes that were uploaded from "${batchName}"?`)) return;

        setLoading(true);
        setMessage(`Deleting ${quotesToDelete.length} quotes from ${batchName}...`);
        try {
            const deletePromises = [];
            for (const q of quotesToDelete) {
                deletePromises.push(deleteDoc(doc(db, "dailyQuotes", q.dbId)));
                if (deletePromises.length >= 50) {
                    await Promise.all(deletePromises);
                    deletePromises.length = 0;
                }
            }
            if (deletePromises.length > 0) {
                await Promise.all(deletePromises);
            }
            setMessage(`Successfully deleted ${quotesToDelete.length} quotes from ${batchName}.`);
            fetchQuotes();
        } catch (error) {
            console.error("Error deleting CSV batch:", error);
            setMessage("Failed to delete CSV batch: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    const cancelEdit = () => {
        setEditingId(null);
        setFormData({ text: "", author: "", category: "General", image: "", textColor: "#ffffff", backgroundType: "image", backgroundColor: "#FFF7F3" });
        setImageFile(null);
        setMessage("");
    };

    // Bulk Upload Handler (Updated for Sparse Categories format)
    const handleBulkUpload = async () => {
        if (!csvFile) {
            alert("Please upload a CSV file.");
            return;
        }

        setBulkLoading(true);
        setBulkProgress(0);

        Papa.parse(csvFile, {
            header: true, // we assume headers exist
            skipEmptyLines: true,
            complete: async (results) => {
                const data = results.data;
                const batchSize = 500;
                let batch = writeBatch(db);
                let count = 0;
                let successCount = 0;
                const currentBatchId = Date.now().toString();

                let activeCategory = "General"; // Track active category across rows

                try {
                    for (let i = 0; i < data.length; i++) {
                        const rawRow = data[i];
                        
                        // Find keys dynamically to be flexible with user's typos
                        const catKey = Object.keys(rawRow).find(k => k.toLowerCase().includes('categ'));
                        const quoteKey = Object.keys(rawRow).find(k => k.toLowerCase().includes('quote') || k.toLowerCase().includes('text'));
                        const authorKey = Object.keys(rawRow).find(k => k.toLowerCase().includes('author'));
                        
                        const rowCat = catKey ? rawRow[catKey] : "";
                        if (rowCat && rowCat.trim() !== "") {
                            activeCategory = rowCat.trim();
                        }

                        const quoteText = quoteKey ? (rawRow[quoteKey] || "").trim() : "";
                        const authorText = authorKey ? (rawRow[authorKey] || "").trim() : "";

                        // If there is no quote text on this row, it's likely just a category header row. Skip creating a quote.
                        if (!quoteText) {
                            continue;
                        }

                        const quoteRef = doc(collection(db, "dailyQuotes"));
                        const quoteData = {
                            text: quoteText,
                            author: authorText,
                            category: activeCategory,
                            textColor: "#ffffff", // Default white text for image backgrounds
                            createdAt: serverTimestamp(),
                            updatedAt: serverTimestamp(),
                            uploadBatchId: currentBatchId,
                            uploadFileName: csvFile.name
                        };

                        batch.set(quoteRef, quoteData);
                        count++;
                        successCount++;

                        setBulkProgress(Math.round((i / data.length) * 100));

                        if (count === batchSize) {
                            await batch.commit();
                            batch = writeBatch(db);
                            count = 0;
                        }
                    }

                    if (count > 0) {
                        await batch.commit();
                    }

                    alert(`Successfully uploaded ${successCount} quotes!`);
                    setBulkUploadOpen(false);
                    setCsvFile(null);
                    setBulkImages([]);
                    fetchQuotes();

                } catch (error) {
                    console.error("Error during bulk upload:", error);
                    alert("An error occurred during bulk upload: " + error.message);
                } finally {
                    setBulkLoading(false);
                    setBulkProgress(0);
                }
            },
            error: (err) => {
                console.error("CSV Parse Error:", err);
                alert("Failed to parse CSV file.");
                setBulkLoading(false);
            }
        });
    };

    // Search and Infinite Scroll Logic
    const filteredQuotes = quotes.filter(q =>
        q.text.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const displayedQuotes = filteredQuotes.slice(0, visibleCount);

    const lastQuoteElementRef = useCallback(node => {
        if (loading) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && visibleCount < filteredQuotes.length) {
                setVisibleCount(prevCount => prevCount + 10);
            }
        });
        if (node) observer.current.observe(node);
    }, [loading, visibleCount, filteredQuotes.length]);

    const styles = {
        container: { padding: '20px', width: '100%', maxWidth: '1200px', margin: '0 auto', fontFamily: "'Syne', sans-serif", boxSizing: 'border-box' },
        header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap' },
        title: { fontSize: 'clamp(1.5rem, 4vw, 2.5rem)', fontWeight: '400', color: '#003049', margin: 0 },
        mainFlex: { display: 'flex', gap: '30px', alignItems: 'flex-start', flexWrap: 'wrap', width: '100%' },
        formSection: { flex: '1 1 300px', width: '100%', background: 'white', padding: 'clamp(15px, 3vw, 30px)', borderRadius: '15px', boxShadow: '0 10px 30px rgba(0,0,0,0.05)', boxSizing: 'border-box' },
        listSection: { flex: '1 1 300px', width: '100%', padding: '10px', maxHeight: '800px', overflowY: 'auto', boxSizing: 'border-box' },
        inputGroup: { display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '15px' },
        label: { fontSize: '14px', fontWeight: '600', color: '#003049' },
        input: { padding: '12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', fontFamily: "'Syne', sans-serif", outline: 'none', width: '100%', boxSizing: 'border-box' },
        textarea: { padding: '12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', fontFamily: "'Syne', sans-serif", outline: 'none', minHeight: '100px', resize: 'vertical', width: '100%', boxSizing: 'border-box' },
        submitBtn: { padding: '15px', background: '#F07167', color: 'white', borderRadius: '30px', cursor: 'pointer', border: 'none', fontWeight: 'bold', fontSize: 'clamp(14px, 2vw, 16px)', width: '100%', marginTop: '10px', boxShadow: '0 4px 15px rgba(240,113,103,0.3)', flex: 1 },
        bulkBtn: { padding: '10px 20px', background: '#003049', color: 'white', borderRadius: '30px', cursor: 'pointer', border: 'none', fontWeight: 'bold' },
        msg: { padding: '15px', background: '#eefbf3', color: '#155724', borderRadius: '8px', marginBottom: '20px', border: '1px solid #c8e6c9', fontSize: '14px' },
        errorMsg: { padding: '15px', background: '#faeced', color: '#721c24', borderRadius: '8px', marginBottom: '20px', border: '1px solid #f5c6cb', fontSize: '14px' },
        listTitle: { fontSize: 'clamp(1.2rem, 3vw, 1.5rem)', fontWeight: '400', color: '#003049', marginBottom: '20px' },
        quoteCard: { padding: '15px', border: 'none', borderRadius: '15px', marginBottom: '15px', background: 'white', display: 'flex', gap: '15px', alignItems: 'center', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', position: 'relative', flexWrap: 'wrap' },
        quoteImg: { width: 'clamp(80px, 20vw, 120px)', height: 'clamp(60px, 15vw, 80px)', borderRadius: '8px', objectFit: 'cover', border: '1px solid #eee' },
        quoteInfo: { flex: '1 1 150px' },
        quoteText: { margin: '0 0 5px 0', fontSize: '14px', fontWeight: '500', color: '#333', paddingRight: '40px', fontStyle: 'italic' },
        cardActions: { position: 'absolute', top: '10px', right: '10px', display: 'flex', flexDirection: 'row', gap: '5px' },
        actionBtn: { padding: '6px', border: 'none', background: 'transparent', cursor: 'pointer', color: '#003049' },
        deleteBtn: { padding: '6px', border: 'none', background: 'transparent', cursor: 'pointer', color: '#F07167' },
        searchBar: { padding: '12px', border: '1px solid #ddd', borderRadius: '30px', width: '100%', marginBottom: '15px', boxSizing: 'border-box' }
    };

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h1 style={styles.title}>{editingId ? "Edit Quote" : "Add New Daily Quote"}</h1>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <button onClick={handleDeleteAll} style={{ ...styles.bulkBtn, backgroundColor: '#F07167' }}>Delete All Quotes</button>
                    <button onClick={handleDeleteByBatch} style={{ ...styles.bulkBtn, backgroundColor: '#E07A5F' }}>Delete By CSV</button>
                    <button onClick={() => setBulkUploadOpen(true)} style={styles.bulkBtn}>Bulk Upload</button>
                </div>
            </div>

            <div style={styles.mainFlex}>
                {/* FORM */}
                <div style={styles.formSection}>
                    {message && (
                        <div style={message.includes("Failed") || message.includes("Please") ? styles.errorMsg : styles.msg}>
                            {message}
                        </div>
                    )}

                    <form onSubmit={handleSubmit}>
                        <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                            <div style={{ ...styles.inputGroup, flex: 1 }}>
                                <label style={styles.label}>Category</label>
                                <input type="text" name="category" value={formData.category} onChange={handleInput} style={styles.input} required placeholder="e.g. Self-growth" />
                            </div>
                            <div style={{ ...styles.inputGroup, flex: 1 }}>
                                <label style={styles.label}>Author</label>
                                <input type="text" name="author" value={formData.author} onChange={handleInput} style={styles.input} placeholder="e.g. Maya Angelou" />
                            </div>
                        </div>

                        <div style={styles.inputGroup}>
                            <label style={styles.label}>Quote Text</label>
                            <textarea name="text" value={formData.text} onChange={handleInput} style={styles.textarea} required placeholder="Enter the quote text..." />
                        </div>

                        {!editingId && (
                            <>
                                <div style={styles.inputGroup}>
                                    <label style={styles.label}>Background Type</label>
                                    <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                                            <input type="radio" name="backgroundType" value="image" checked={formData.backgroundType === "image"} onChange={handleInput} />
                                            <span>Image</span>
                                        </label>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                                            <input type="radio" name="backgroundType" value="color" checked={formData.backgroundType === "color"} onChange={handleInput} />
                                            <span>Plain Color</span>
                                        </label>
                                    </div>
                                </div>

                                {formData.backgroundType === "image" ? (
                                    <div style={styles.inputGroup}>
                                        <label style={styles.label}>Background Image</label>
                                        <input type="file" accept="image/*" onChange={handleFileChange} style={styles.input} required={!editingId && !formData.image} />
                                        {formData.image && <small style={{ color: '#666' }}>Current Image uploaded</small>}
                                    </div>
                                ) : (
                                    <div style={styles.inputGroup}>
                                        <label style={styles.label}>Background Color</label>
                                        <div style={{ display: 'flex', gap: '10px' }}>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                <input type="radio" name="backgroundColor" value="#FFF7F3" checked={formData.backgroundColor === "#FFF7F3"} onChange={handleInput} />
                                                <span style={{ display: 'inline-block', width: '20px', height: '20px', backgroundColor: '#FFF7F3', border: '1px solid #ddd', borderRadius: '4px' }}></span>
                                                Off-White
                                            </label>
                                        </div>
                                    </div>
                                )}

                                <div style={styles.inputGroup}>
                                    <label style={styles.label}>Text Color (Light / Dark)</label>
                                    <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                                            <input type="radio" name="textColor" value="#ffffff" checked={formData.textColor === "#ffffff"} onChange={handleInput} />
                                            <span style={{ color: '#003049', padding: '2px 8px', borderRadius: '4px' }}>White Text</span>
                                        </label>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                                            <input type="radio" name="textColor" value="#333333" checked={formData.textColor === "#333333"} onChange={handleInput} />
                                            <span style={{ color: '#333', background: '#eee', padding: '2px 8px', borderRadius: '4px' }}>Dark Text</span>
                                        </label>
                                    </div>
                                </div>
                            </>
                        )}

                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                            <button type="submit" style={styles.submitBtn} disabled={loading}>
                                {loading ? "Saving..." : (editingId ? "Update Quote" : "Add Quote")}
                            </button>
                            <button type="button" onClick={() => setPreviewOpen(true)} style={{ ...styles.submitBtn, background: '#f08e55', boxShadow: '0 4px 15px rgba(74,144,226,0.3)' }}>
                                Preview
                            </button>
                            {editingId && (
                                <button type="button" onClick={cancelEdit} style={{ ...styles.submitBtn, background: '#eee', color: '#333', boxShadow: 'none' }}>
                                    Cancel
                                </button>
                            )}
                        </div>
                    </form>
                </div>

                {/* LIST */}
                <div className="no-scrollbar" style={styles.listSection}>
                    <h2 style={styles.listTitle}>Live Quotes ({quotes.length})</h2>
                    <input
                        type="text"
                        placeholder="Search quotes..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={styles.searchBar}
                    />
                    {displayedQuotes.length === 0 ? (
                        <p style={{ fontSize: '14px', color: '#777' }}>No daily quotes found.</p>
                    ) : (
                        displayedQuotes.map((q, index) => {
                            const isLast = displayedQuotes.length === index + 1;
                            return (
                                <div key={q.dbId} style={styles.quoteCard} ref={isLast ? lastQuoteElementRef : null}>
                                    {q.backgroundColor ? (
                                        <div style={{ ...styles.quoteImg, backgroundColor: q.backgroundColor, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#aaa', fontSize: '10px' }}>Color</div>
                                    ) : (
                                        <img src={q.image || "/placeholder.jpg"} alt="Quote Background" style={styles.quoteImg} />
                                    )}
                                    <div style={styles.quoteInfo}>
                                        <p style={styles.quoteText}>"{q.text}"</p>
                                        <small style={{ color: '#666', display: 'block', marginBottom: '4px' }}>
                                            {q.author ? `- ${q.author}` : ''}
                                        </small>
                                        <span style={{ fontSize: '11px', background: '#eee', padding: '2px 6px', borderRadius: '10px', color: '#333' }}>
                                            {q.category || 'General'}
                                        </span>
                                    </div>
                                    <div style={styles.cardActions}>
                                        <button type="button" onClick={() => handleEdit(q)} style={styles.actionBtn} title="Edit Quote">
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M12 20h9"></path>
                                                <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
                                            </svg>
                                        </button>
                                        <button type="button" onClick={() => handleDelete(q)} style={styles.deleteBtn} title="Delete Quote">
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="3 6 5 6 21 6"></polyline>
                                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            )
                        })
                    )}
                </div>
            </div>

            {/* PREVIEW MODAL */}
            <Modal
                title="Quote Preview"
                open={previewOpen}
                onCancel={() => setPreviewOpen(false)}
                footer={null}
                width={1000}
                centered
            >
                <div style={{
                    position: 'relative',
                    width: '100%',
                    height: '500px',
                    borderRadius: '24px',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: formData.backgroundType === 'color' ? formData.backgroundColor : '#f5f5f5'
                }}>
                    {formData.backgroundType === 'image' && (
                        (imageFile || formData.image) ? (
                            <img
                                src={imageFile ? URL.createObjectURL(imageFile) : formData.image}
                                alt="preview background"
                                style={{
                                    position: 'absolute',
                                    top: 0, left: 0, right: 0, bottom: 0,
                                    width: '100%', height: '100%',
                                    objectFit: 'cover'
                                }}
                            />
                        ) : (
                            <div style={{ position: 'absolute', color: '#aaa' }}>No Image Selected</div>
                        )
                    )}

                    {/* Overlay to ensure text readability similar to the blog page */}
                    {formData.backgroundType === 'image' && (
                        <div style={{
                            position: 'absolute',
                            top: 0, left: 0, right: 0, bottom: 0,
                            backgroundColor: 'rgba(0,0,0,0.2)',
                            zIndex: 5
                        }} />
                    )}

                    <h2 style={{
                        position: 'relative',
                        color: formData.textColor || '#ffffff',
                        fontSize: '32px',
                        fontWeight: '400',
                        textAlign: 'center',
                        maxWidth: '80%',
                        zIndex: 10,
                        textShadow: formData.textColor === '#333333' ? 'none' : '0 2px 10px rgba(0,0,0,0.5)',
                        fontFamily: "'Syne', sans-serif"
                    }}>
                        "{formData.text || 'Your quote will appear here...'}"
                    </h2>
                </div>
            </Modal>

            {/* BULK UPLOAD MODAL */}
            <Modal
                title="Bulk Upload Quotes (CSV)"
                open={bulkUploadOpen}
                onCancel={() => setBulkUploadOpen(false)}
                footer={null}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                    <p style={{ fontSize: '14px', color: '#555' }}>
                        Upload a CSV file containing the following columns: <br />
                        <b>text, textColor, backgroundType, backgroundColor, imageFileName</b><br />
                        <i>Example: "Some quote", "#ffffff", "color", "#FFF7F3", ""</i><br />
                        <i>Example: "Another quote", "#333333", "image", "", "bg1.jpg"</i>
                    </p>
                    <div>
                        <label style={styles.label}>CSV File</label>
                        <input type="file" accept=".csv" onChange={(e) => setCsvFile(e.target.files[0])} style={styles.input} />
                    </div>
                    <div>
                        <label style={styles.label}>Images (if used in CSV)</label>
                        <input type="file" multiple accept="image/*" onChange={(e) => setBulkImages(e.target.files)} style={styles.input} />
                        <small style={{ color: '#666' }}>Select all images referenced in your CSV's <i>imageFileName</i> column.</small>
                    </div>
                    {bulkProgress > 0 && (
                        <div style={{ width: '100%', backgroundColor: '#eee', borderRadius: '4px' }}>
                            <div style={{ width: `${bulkProgress}%`, height: '10px', backgroundColor: '#4caf50', borderRadius: '4px', transition: 'width 0.3s' }}></div>
                        </div>
                    )}
                    <button onClick={handleBulkUpload} style={styles.submitBtn} disabled={bulkLoading}>
                        {bulkLoading ? `Uploading... ${bulkProgress}%` : 'Upload Quotes'}
                    </button>
                </div>
            </Modal>
        </div>
    );
}
